/**
 * Unified Order Activity Log Format
 *
 * Standardizes ALL log entries with fixed emojis for each role:
 * - 👤 Manager (universal silhouette)
 * - 🚚 Courier
 * - ⚙️ System
 *
 * Format: "{emoji} [{role}] · {time} · {message}"
 *
 * This is the SINGLE SOURCE OF TRUTH for the order activity stream.
 * The Live Console reads ONLY from internal_notes using this format.
 */

export type DeliveryLogRole = "manager" | "courier" | "system";

// Fixed emojis that never change
export const ROLE_EMOJIS: Record<DeliveryLogRole, string> = {
  manager: "🧑‍💼", // Business person - original emoji
  courier: "🚚",
  system: "⚙️",
};

// Role tokens for parsing
export const ROLE_TOKENS: Record<DeliveryLogRole, string> = {
  manager: "[manager]",
  courier: "[courier]",
  system: "[system]",
};

// Activity action types for structured logging
export type ActivityAction =
  | "status_changed"
  | "address_updated"
  | "assigned_to_session"
  | "removed_from_session"
  | "payment_received"
  | "order_received"
  | "order_edited"
  | "acknowledged"
  | "cant_take"
  | "out_for_delivery"
  | "delivered"
  | "delivery_failed"
  | "retry_delivery"
  | "comment";

// Human-readable status labels for log messages
export const STATUS_LABELS = {
  en: {
    New: "New",
    Completed: "Ready",
    Ready: "Ready",
    "Out for Delivery": "Out for Delivery",
    Delivered: "Delivered",
    "Delivery Failed": "Delivery Failed",
    Cancelled: "Cancelled",
    "Awaiting Payment": "Awaiting Payment",
  } as Record<string, string>,
  ka: {
    New: "ახალი",
    Completed: "მზადაა",
    Ready: "მზადაა",
    "Out for Delivery": "გზაშია",
    Delivered: "მიტანილია",
    "Delivery Failed": "ვერ მიიტანა",
    Cancelled: "გაუქმებულია",
    "Awaiting Payment": "გადახდის მოლოდინში",
  } as Record<string, string>,
};

// Human-readable message templates for activity actions
const ACTION_TEMPLATES = {
  status_changed: {
    en: (status: string) => `Status changed to ${STATUS_LABELS.en[status] || status}`,
    ka: (status: string) => `სტატუსი შეიცვალა: ${STATUS_LABELS.ka[status] || status}`,
  },
  address_updated: {
    en: () => "Delivery address was updated",
    ka: () => "მისამართი განახლდა",
  },
  assigned_to_session: {
    en: (label?: string) => (label ? `Assigned to session "${label}"` : "Assigned to delivery session"),
    ka: (label?: string) => (label ? `მიენიჭა სესიას "${label}"` : "მიენიჭა მიტანის სესიას"),
  },
  removed_from_session: {
    en: () => "Removed from delivery session",
    ka: () => "წაიშალა მიტანის სესიიდან",
  },
  payment_received: {
    en: () => "Payment received",
    ka: () => "გადახდა მიღებულია",
  },
  order_received: {
    en: () => "Order received",
    ka: () => "შეკვეთა მიღებულია",
  },
  order_edited: {
    en: () => "Order details updated",
    ka: () => "შეკვეთის დეტალები განახლდა",
  },
  acknowledged: {
    en: () => "Acknowledged",
    ka: () => "დადასტურებულია",
  },
  cant_take: {
    en: (reason: string) => `Can't take – ${reason}`,
    ka: (reason: string) => `ვერ ვიღებ – ${reason}`,
  },
  out_for_delivery: {
    en: () => "Out for delivery",
    ka: () => "გზაშია",
  },
  delivered: {
    en: () => "Delivered",
    ka: () => "მიტანილია",
  },
  delivery_failed: {
    en: (reason?: string) => (reason ? `Delivery failed – ${reason}` : "Delivery failed"),
    ka: (reason?: string) => (reason ? `ვერ მიიტანა – ${reason}` : "ვერ მიიტანა"),
  },
  retry_delivery: {
    en: () => "Delivery retry initiated",
    ka: () => "ხელახალი მიტანა დაიწყო",
  },
  comment: {
    en: (message: string) => message,
    ka: (message: string) => message,
  },
};

/**
 * Formats the current time in HH:MM format for Tbilisi timezone
 * Always uses 24-hour format (en-GB) for consistency with edge functions
 */
function formatLogTimestamp(language: "en" | "ka" = "en"): string {
  const now = new Date();
  // Always use en-GB for reliable part extraction, then swap month for Georgian
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tbilisi",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  
  const enMonth = parts.find(p => p.type === "month")?.value || "";
  const day = parts.find(p => p.type === "day")?.value;
  const year = parts.find(p => p.type === "year")?.value;
  const hour = parts.find(p => p.type === "hour")?.value;
  const minute = parts.find(p => p.type === "minute")?.value;
  
  // Georgian month abbreviations (reliable fallback since ka-GE may not be supported)
  const KA_MONTHS: Record<string, string> = {
    "Jan": "იან.", "Feb": "თებ.", "Mar": "მარ.", "Apr": "აპრ.",
    "May": "მაი.", "Jun": "ივნ.", "Jul": "ივლ.", "Aug": "აგვ.",
    "Sep": "სექ.", "Oct": "ოქტ.", "Nov": "ნოე.", "Dec": "დეკ.",
  };
  
  const month = language === "ka" ? (KA_MONTHS[enMonth] || enMonth) : enMonth;
  return `${month} ${day}, ${year} · ${hour}:${minute}`;
}

/**
 * Creates a new standardized log entry (legacy function, kept for compatibility)
 */
export function createLogEntry(role: DeliveryLogRole, message: string, language: "en" | "ka" = "en"): string {
  const token = ROLE_TOKENS[role];
  const time = formatLogTimestamp(language);

  return `${token} · ${time} · ${message}`;
}

/**
 * Creates a new activity log entry with standardized action templates
 */
export function createActivityEntry(
  role: DeliveryLogRole,
  action: ActivityAction,
  details?: string,
  language: "en" | "ka" = "en",
): string {
  const token = ROLE_TOKENS[role];
  const time = formatLogTimestamp(language);

  // Get message from template
  const template = ACTION_TEMPLATES[action];
  const message = template[language](details || "");

  return `${token} · ${time} · ${message}`;
}

/**
 * Appends a new entry to existing notes
 * With deduplication: skips if last line has same role and message content
 */
export function appendLogEntry(
  existingNotes: string | null,
  role: DeliveryLogRole,
  message: string,
  language: "en" | "ka" = "en",
): string {
  const entry = createLogEntry(role, message, language);
  
  // Dedupe: skip if last line has same role and message
  if (existingNotes) {
    const lines = existingNotes.trim().split("\n").filter(Boolean);
    const lastLine = lines[lines.length - 1];
    if (lastLine) {
      const parts = lastLine.split(" · ");
      const lastRole = parseLogRole(lastLine);
      const lastMessage = parts.length >= 3 ? parts.slice(2).join(" · ") : "";
      
      if (lastRole === role && lastMessage === message) {
        return existingNotes; // Return unchanged - duplicate blocked
      }
    }
  }
  
  return existingNotes ? `${existingNotes}\n${entry}` : entry;
}

/**
 * Appends a new activity entry to existing notes using action templates
 * With deduplication: skips if last line has same action content
 */
export function appendActivityEntry(
  existingNotes: string | null,
  role: DeliveryLogRole,
  action: ActivityAction,
  details?: string,
  language: "en" | "ka" = "en",
): string {
  const entry = createActivityEntry(role, action, details, language);
  
  // Dedupe: skip if last line is identical (ignoring timestamp)
  if (existingNotes) {
    const lines = existingNotes.trim().split("\n").filter(Boolean);
    const lastLine = lines[lines.length - 1];
    if (lastLine) {
      const parts = lastLine.split(" · ");
      const lastRole = parseLogRole(lastLine);
      const lastMessage = parts.length >= 3 ? parts.slice(2).join(" · ") : "";
      
      const entryParts = entry.split(" · ");
      const entryMessage = entryParts.length >= 3 ? entryParts.slice(2).join(" · ") : "";
      
      if (lastRole === role && lastMessage === entryMessage) {
        return existingNotes; // Return unchanged - duplicate blocked
      }
    }
  }
  
  return existingNotes ? `${existingNotes}\n${entry}` : entry;
}

/**
 * Parses a log line to extract role (for display purposes)
 * Returns null if no role token found (legacy format)
 */
export function parseLogRole(line: string): DeliveryLogRole | null {
  if (line.includes("[manager]")) return "manager";
  if (line.includes("[courier]")) return "courier";
  if (line.includes("[system]")) return "system";

  // Check for emojis
  if (line.includes("🧑‍💼")) return "manager";
  if (line.includes("🚚")) return "courier";
  if (line.includes("⚙️")) return "system";

  // Legacy fallback: old emojis (for existing DB data)
  if (line.includes("👤")) return "manager";
  if (line.includes("🤖")) return "system";

  // Additional legacy checks
  if (line.toLowerCase().includes("courier") || line.includes("კურიერი")) return "courier";

  return null;
}

/**
 * Gets the display label for a role based on language
 */
export function getRoleLabel(
  role: DeliveryLogRole,
  translations: { manager?: string; courier?: string; system?: string },
): string {
  switch (role) {
    case "manager":
      return translations.manager || "Manager";
    case "courier":
      return translations.courier || "Courier";
    case "system":
      return translations.system || "System";
    default:
      return "";
  }
}

/**
 * Formats a log line for display, replacing tokens with translated labels
 */
export function formatLogLineForDisplay(
  line: string,
  translations: { manager?: string; courier?: string; system?: string },
): string {
  let formatted = line;

  // Replace tokens with translated labels (keep the emoji)
  if (formatted.includes("[manager]")) {
    formatted = formatted.replace("[manager]", translations.manager || "Manager");
  }
  if (formatted.includes("[courier]")) {
    formatted = formatted.replace("[courier]", translations.courier || "Courier");
  }
  if (formatted.includes("[system]")) {
    formatted = formatted.replace("[system]", translations.system || "System");
  }

  return formatted;
}

/**
 * Strips role token from log line for display when using RoleIcon
 * Input:  "[manager] · 14:20 · message"
 * Output: "14:20 · message"
 */
export function stripRoleTokenForDisplay(line: string): string {
  return line
    .replace(/\[manager\]\s*·\s*/, "")
    .replace(/\[courier\]\s*·\s*/, "")
    .replace(/\[system\]\s*·\s*/, "");
}

/**
 * Strips role token AND date from log line for compact table display
 * Keeps only "HH:mm · message" for daily operational views
 * 
 * Handles both formats:
 *   New: "[manager] · Feb 23, 2026 · 14:30 · message" -> "14:30 · message"
 *   Old: "[manager] · 14:30 · message" -> "14:30 · message"
 */
export function stripToTimeAndMessage(line: string): string {
  let stripped = stripRoleTokenForDisplay(line);
  // Strip date portion (any script month + day, year ·) before HH:mm
  stripped = stripped.replace(/^.+?\d{4}\s*·\s*/, "");
  return stripped;
}

/**
 * Parse acknowledgement state from internal_notes
 * Returns: 'none' | 'acknowledged' | 'cant_take'
 * Uses content-based detection (not emoji-based) for reliability
 */
export function parseAcknowledgementState(
  notes: string | undefined,
  currentStatus: string,
): "none" | "acknowledged" | "cant_take" {
  // If already out for delivery or beyond, acknowledgement doesn't matter
  if (currentStatus === "Out for Delivery" || currentStatus === "Delivered" || currentStatus === "Delivery Failed") {
    return "acknowledged";
  }

  if (!notes) return "none";

  const lines = notes.split("\n").filter(Boolean);

  // Scan from newest to oldest
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();

    // Manager resets acknowledgement state (token or emoji)
    if (line.includes("[manager]") || line.includes("🧑‍💼") || line.includes("👤")) {
      return "none";
    }

    // Check for courier entries (token or emoji)
    const isCourierEntry = line.includes("[courier]") || line.includes("🚚");

    if (isCourierEntry) {
      // Check for can't take first (more specific)
      if (lowerLine.includes("can't take") || lowerLine.includes("cant take") || lowerLine.includes("ვერ ვიღებ")) {
        return "cant_take";
      }

      // Check for acknowledgement by content
      if (lowerLine.includes("acknowledged") || lowerLine.includes("დადასტურებულია")) {
        return "acknowledged";
      }
    }
  }

  return "none";
}

/**
 * Get the human-readable status label for a given status
 */
export function getStatusLabel(status: string, language: "en" | "ka" = "en"): string {
  return STATUS_LABELS[language][status] || status;
}

/**
 * Determines if a log line should be visible in surface views (tables)
 * Simple rule: Show manager and courier entries, hide system entries
 * Exception: Always show critical failures
 */
export function shouldShowInSurfaceView(line: string): boolean {
  const role = parseLogRole(line);
  const lowerLine = line.toLowerCase();
  
  // Critical failures - ALWAYS show regardless of role
  if (
    lowerLine.includes("failed") ||
    lowerLine.includes("ვერ მიიტანა") ||
    lowerLine.includes("cancelled") ||
    lowerLine.includes("გაუქმებულია")
  ) {
    return true;
  }
  
  // Simple rule: human roles visible, system hidden
  if (role === "manager" || role === "courier") return true;
  if (role === "system") return false;
  
  // Unknown role: default to show (legacy data)
  return true;
}

/**
 * Returns the last log entry that should be shown in surface views
 * Skips hidden system events
 */
export function getLastSurfaceEntry(notes: string | null): string | null {
  if (!notes) return null;

  const lines = notes.trim().split("\n").filter(Boolean);

  // Scan from newest to oldest, return first visible entry
  for (let i = lines.length - 1; i >= 0; i--) {
    if (shouldShowInSurfaceView(lines[i])) {
      return lines[i];
    }
  }

  return null; // All entries are hidden system events
}

/**
 * Returns all visible entries for surface views (for history display)
 */
export function getSurfaceVisibleEntries(notes: string | null): string[] {
  if (!notes) return [];

  return notes
    .trim()
    .split("\n")
    .filter(Boolean)
    .filter((line) => shouldShowInSurfaceView(line));
}

/**
 * Formats delivered order log entry for compact display
 * Returns: "✓ Delivered · 21:12" or "✓ მიტანილია · 21:12"
 * Full log still available on hover/click
 * @deprecated Use getDeliveredLogEntry() for RoleIcon-compatible display
 */
export function formatDeliveredSummary(
  notes: string | null,
  language: "en" | "ka" = "en",
): string | null {
  if (!notes) return null;

  const lines = notes.trim().split("\n").filter(Boolean);

  // Scan from newest to find delivery confirmation
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();

    if (lowerLine.includes("delivered") || lowerLine.includes("მიტანილია") || lowerLine.includes("ჩაბარდა")) {
      // Extract time from line (format: "... · HH:MM · ...")
      const timeMatch = line.match(/(\d{2}:\d{2})/);
      const time = timeMatch ? timeMatch[1] : "";
      const label = language === "ka" ? "მიტანილია" : "Delivered";

      return `✓ ${label}${time ? ` · ${time}` : ""}`;
    }
  }

  return null;
}

/**
 * Parses delivered order log entry for RoleIcon-compatible display
 * Returns structured data: { role, text, time } for consistent log formatting
 * Used by TodayTable and other surfaces to show delivered status with proper role attribution
 */
export function getDeliveredLogEntry(
  notes: string | null,
  language: "en" | "ka" = "en",
): { role: DeliveryLogRole; text: string; time: string } | null {
  if (!notes) return null;

  const lines = notes.trim().split("\n").filter(Boolean);

  // Scan from newest to find delivery confirmation
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();

    if (lowerLine.includes("delivered") || lowerLine.includes("მიტანილია") || lowerLine.includes("ჩაბარდა")) {
      const role = parseLogRole(line) || "courier"; // Default to courier for delivered actions
      const timeMatch = line.match(/(\d{2}:\d{2})/);
      const time = timeMatch ? timeMatch[1] : "";
      const text = language === "ka" ? "მიტანილია" : "Delivered";

      return { role, text, time };
    }
  }

  return null;
}

/**
 * Get the last meaningful log entry with role and message
 * Used by both real UI and demo components for consistent parsing
 */
export function getLastLogEntry(notes: string | null): { role: DeliveryLogRole; message: string } | null {
  if (!notes) return null;
  
  const lines = notes.trim().split("\n").filter(Boolean);
  if (lines.length === 0) return null;
  
  // Scan from newest to find first entry with a valid role
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    const role = parseLogRole(line);
    
    if (!role) continue;
    
    // Extract message from line format: "[role] · HH:MM · message"
    const parts = line.split(' · ');
    const message = parts.length >= 3 ? parts.slice(2).join(' · ').trim() : "";
    
    return { role, message };
  }
  
  return null;
}
